import React from 'react';
import {Distribution, DrawerType, FiduciaryMember, RevocableTrust, Trustee} from "../../model/EstateFlowchartTypes";
import {IrrevocableTrust} from "../../model/IrrevocableTrust";
import {
    convertStateAbbreviation,
    flattenMembers,
    getFamilyMemberByMemberId,
    getMemberNameById,
    getMembersList,
    getOrderedEstateMemberList
} from "../../utils/DefUtils";
import {useAppSelector} from "../../../store/hooks";
import {familyMembersSelector, listOfStatesSelector} from "../../slices/EstateFlowChartSlice";
import moment from "moment/moment";
import {CardDetailFieldsSections, drawerFieldConfig, isDrawerFieldConfigKey} from "../../utils/DrawerUtils";
import {Will} from "../../model/Will";
import {getFiduciarySummary} from "../../slices/FiduciarySlice";
import {selectReleaseToggles} from "../../../ReleaseToggles/releaseTogglesSlice";
import {MemberGroup} from 'src/ClientManagement/models/InvestorGroupType';
import {Miscellaneous} from "../../model/Miscellaneous";

interface SideDrawerContentTrustProps {
    sideDrawerItem: RevocableTrust | IrrevocableTrust | Will | Miscellaneous | Distribution;
    trustType: DrawerType;
    isInReportSplitView?: boolean,
    sideDrawerFields?: CardDetailFieldsSections
}

const SideDrawerContentTrust: React.FC<SideDrawerContentTrustProps> = ({
                                                                           sideDrawerItem,
                                                                           trustType,
                                                                           isInReportSplitView,
                                                                           sideDrawerFields,
                                                                       }) => {
    const rawFamilyMembers = useAppSelector(familyMembersSelector);
    const unitedStates = useAppSelector(listOfStatesSelector);
    const fiduciaryAssignments = useAppSelector(getFiduciarySummary);
    const releaseToggles = useAppSelector(selectReleaseToggles);

    const renderFieldRow = (label: string, value: string | undefined | null, testId: string) => {
        return value ? (
            <div className="field-row" data-testid={testId} key={testId}>
                <label className="field-label">{label}</label>
                <label className="field-value">{value}</label>
            </div>
        ) : null;
    };

    const renderPOATitle = (label: string, property: string) => {
        return <div className="poa-section">
            <div className="poa-title" key={property}>
                {label}
            </div>
        </div>
    }

    const renderSectionHeader = (label: string, property: string) => {
        return <label className="section-title" key={property}>
            {label}
        </label>
    }


    const renderFieldWithOrderedList = (label: string, members: string[] | undefined, testId: string) => {
        if (!members || members.length === 0) return null;
        return (
            <div key={testId}>
                <div className="field-row" data-testid={testId}>
                    <label className="field-label">{label}</label>
                    <ol className="field-ordered-list">
                        {members.map((member, index) => (
                            <li key={`${member.replace(' ', '-').toLowerCase()}-${index}`} className="field-value">
                                {member}
                            </li>
                        ))}
                    </ol>
                </div>
                <div className="spacer"></div>
            </div>
        );
    };

    const renderFieldWithUnOrderedList = (label: string, listItems: string[] | undefined, testId?: string) => {
        if (!listItems || listItems.length === 0) return null;
        return (
            <div key={testId}>
                <div className="field-row" data-testid={testId}>
                    <label className="field-label">{label}</label>
                    <ul className="field-ordered-list">
                        {listItems.map((item) => (
                            <li key={`${item.replace(' ', '-').toLowerCase()}`} className="field-value">{item}</li>
                        ))}
                    </ul>
                </div>
                <div className="spacer"></div>
            </div>
        )
    };

    /**
     * Returns the display name for the termination member, respecting the release toggle and customName.
     * @param terminationMember - The member object from the trust item
     * @param rawFamilyMembers - The full family member list
     */
    const getTerminationMemberDisplayName = (
        terminationMember: Trustee | undefined,
        rawFamilyMembers: MemberGroup,
    ): string | undefined => {
        if (releaseToggles?.enableEstateFlowchartMVPRelease
            && terminationMember?.customName) return terminationMember.customName;
        const familyMember = getFamilyMemberByMemberId(terminationMember?.memberId, rawFamilyMembers);
        if (familyMember) {
            const name = [familyMember.firstName, familyMember.lastName].filter(Boolean).join(' ');
            return name.trim();
        }
        return undefined;
    };

    /**
     * Renders a field row for trust termination, handling release toggles and custom names.
     * @param label - The label for the field.
     * @param isTermination - Whether the trust terminates upon a specific death
     * @param testId - The test id for the field row.
     * @returns JSX.Element | null
     */
    const renderTerminationField = (label: string, isTermination: boolean, testId: string) => {
        let fieldValue: string | undefined;
        if (!isTermination && "terminationDetails" in sideDrawerItem &&
            sideDrawerItem["terminationDetails"] !== "no termination value") {
            fieldValue = sideDrawerItem["terminationDetails"];
        } else if (isTermination && "firstDeathTerminationMember" in sideDrawerItem) {
            const terminationMember = sideDrawerItem?.firstDeathTerminationMember;
            const displayName = getTerminationMemberDisplayName(
                terminationMember,
                rawFamilyMembers,
            );
            fieldValue = `Upon death of ${displayName}`;
        }
        return (
            <div className="field-row" data-testid={testId} key={testId}>
                <label className="field-label">{label}</label>
                <div className="field-value">
                    {fieldValue}
                </div>
            </div>
        );
    };

    const getListItems = (property: string, primaryWill: boolean, trustValue: any): string[] => {
        switch (property) {
            case "executors": {
                const executors: FiduciaryMember[] | undefined = primaryWill
                    ? fiduciaryAssignments.primaryMember?.executors
                    : fiduciaryAssignments.partnerMember?.executors;
                return executors ? getOrderedEstateMemberList(rawFamilyMembers, executors) : [];
            }
            case "guardiansForMinors": {
                const guardians: FiduciaryMember[] | undefined = primaryWill
                    ? fiduciaryAssignments.primaryMember?.guardiansForMinors
                    : fiduciaryAssignments.partnerMember?.guardiansForMinors;
                return guardians ? getOrderedEstateMemberList(rawFamilyMembers, guardians) : [];
            }
            case "successors":
                return getOrderedEstateMemberList(rawFamilyMembers, trustValue);
            default:
                return [];
        }
    };

    const getTrustValue = (type: string, trustValue: any) => {
        switch (type) {
            case "list":
                return trustValue.length ? flattenMembers(rawFamilyMembers, trustValue) : "";
            case "date":
                return moment(trustValue).format("MM/DD/YYYY");
            case "state":
                return convertStateAbbreviation(unitedStates, trustValue);
            default:
                return trustValue;
        }
    };

    const renderDrawerField = ({property, label, type}: any, flowchartDetails: any) => {
        let trustValue = flowchartDetails[property];
        switch (type) {
            case "sectionHeading":
                if (
                    isInReportSplitView && property === "additionalDetailsHeading" &&
                    trustType === "MISCELLANEOUS" &&
                    !renderSectionTwoConditionally(trustType, flowchartDetails)
                ) {
                    return null;
                }
                return renderSectionHeader(label, property);
            case "orderedList": {
                const primaryWill: boolean = flowchartDetails["primaryWill"];
                const listItems = getListItems(property, primaryWill, trustValue);
                return renderFieldWithOrderedList(label, listItems, property);
            }
            case "unorderedList":
                if (property === "outrightMembers") {
                    return renderFieldWithUnOrderedList(label, trustValue.length ? getMembersList(rawFamilyMembers, trustValue) : [], property);
                } else if (releaseToggles?.enableEstateFlowchartMVPRelease) {
                    return renderFieldWithUnOrderedList(label, trustValue.length ? trustValue.map((item: any) => item.name) : [], property);
                } else {
                    break;
                }
            case "termination":
                return renderTerminationField(label, trustValue, property);
            default:
                trustValue = getTrustValue(type, trustValue);
                return renderFieldRow(label, trustValue, property);
        }
    };

    const renderDrawerContent = (fields: any[], flowchartDetails: any) => {
        return <section>{
            fields.map(field => renderDrawerField(field, flowchartDetails))
        }</section>
    }

    const renderPowersOfAppointment = (powersOfAppointment: any[]) => {
        if (!powersOfAppointment || powersOfAppointment.length === 0) return null;

        return (
            <section data-testid="poa-section">
                {renderSectionHeader(`Powers of Appointment`, `powersOfAppointment`)}
                {powersOfAppointment.map((poa, index) => (
                    <div key={`poa-${poa.poaId}`} className="poa-section" data-testid={"poa-" + index}>
                        <div className="poa-title">Power of Appointment {index + 1}</div>
                        <div className="poa-row">
                            {renderFieldRow(`Type`, poa.type, `type-${index}`)}
                            {renderFieldRow(`Transfer Type`, poa.transferType, `transferType-${index}`)}
                        </div>
                        {renderFieldRow(
                            `Power Holder`,
                            getMemberNameById(rawFamilyMembers, poa.powerHolder) ?? poa.powerHolder,
                            `powerHolder-${index}`
                        )}
                        {renderFieldRow(`Permissible Appointees`, poa.permissibleAppointees, `permissibleAppointees-${index}`)}
                        {renderFieldRow(`Additional Details`, poa.additionalDetails, `additionalDetails-${index}`)}
                    </div>
                ))}
            </section>
        );
    };

    const renderSectionTwoConditionally = (trustType: DrawerType, sideDrawerItem: Miscellaneous | RevocableTrust | IrrevocableTrust | Will | Distribution) => {
        if (trustType !== "MISCELLANEOUS") return true;

        const {additionalDetails, funding, beneficiaries} = sideDrawerItem as Miscellaneous;
        return (!!additionalDetails || !!funding || (Array.isArray(beneficiaries) && beneficiaries.length > 0));
    };

    return (<>
        {!isInReportSplitView &&
            <div>
                {
                    (() => {
                        let section1: any[] = [];
                        if (!releaseToggles?.enableEstateFlowchartMVPRelease && trustType === "CHARITY") {
                            section1 = drawerFieldConfig["CHARITY_WITH_ONLY_NAME"]?.section1 || [];
                        } else if (isDrawerFieldConfigKey(trustType)) {
                            section1 = drawerFieldConfig[trustType]?.section1 || [];
                        }
                        return section1.length !== 0 && renderDrawerContent(section1, sideDrawerItem);
                    })()
                }
                {
                    isDrawerFieldConfigKey(trustType) && drawerFieldConfig[trustType]?.section2.length !== 0 &&
                    renderSectionTwoConditionally(trustType, sideDrawerItem) &&
                    renderDrawerContent(drawerFieldConfig[trustType].section2, sideDrawerItem)
                }
                {
                    ("powersOfAppointment" in sideDrawerItem) && Array.isArray(sideDrawerItem.powersOfAppointment) &&
                    sideDrawerItem.powersOfAppointment.length > 0 &&
                    renderPowersOfAppointment((sideDrawerItem).powersOfAppointment || [])
                }
            </div>
        }
        {
            isInReportSplitView && sideDrawerFields &&
            <div>
                {
                    sideDrawerFields.section1.length > 1 &&
                    renderDrawerContent(sideDrawerFields.section1, sideDrawerItem)
                }
                {
                    sideDrawerFields.section2.length > 1 &&
                    renderDrawerContent(sideDrawerFields.section2, sideDrawerItem)
                }
                {sideDrawerFields.powersOfAppointment.length > 0 && (
                    <>
                        {renderSectionHeader("Power of Appointments", "powersOfAppointment")}
                        {sideDrawerFields.powersOfAppointment.map((poaField) => {
                            if (poaField.label === "Power Holder" && poaField.value) {
                                return renderFieldRow(poaField.label,
                                    getMemberNameById(rawFamilyMembers, poaField.value) ?? poaField.value,
                                    poaField.property)
                            }
                            if (poaField.property === "title" && poaField.value) {
                                return renderPOATitle(poaField.value, poaField.property as string)
                            }
                            return renderFieldRow(poaField.label, poaField.value, poaField.property)
                        })}
                    </>
                )}
            </div>
        }
    </>);
};

export default SideDrawerContentTrust;

